import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Bot, Copy, Check, KeyRound, ArrowUpRight, ArrowRight, MessageSquare, ListChecks,
  ShieldCheck, Rocket, History, Wallet, Eye,
} from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

const MCP_URL = `${import.meta.env.VITE_BACKEND_URL.replace(/\/$/, "")}/mcp`;
// Served as static files from frontend/public/skills — the repo is private and
// the backend image doesn't include them, so the SPA's own origin hosts them.
const SKILLS_BASE = `${window.location.origin}/skills`;
const SERVER_NAME = "whyrank";

const AGENT_SCOPES = ["mcp", "fanout:seed", "sitemap:crawl"];

const CLIENTS = ["Claude Code", "Claude Desktop", "ChatGPT", "Cursor", "Hermes"];

function setupPrompt(key) {
  return `Set up the WHYRANK SEO platform for me. Do every step yourself; only stop to ask me when a step needs a manual action in an app UI.

1. Connect the MCP server
   - Name: ${SERVER_NAME}
   - URL: ${MCP_URL}
   - Transport: Streamable HTTP
   - Header: X-Integration-Key: ${key}
   Claude Code: claude mcp add --transport http ${SERVER_NAME} ${MCP_URL} --header "X-Integration-Key: ${key}"
   Other clients: add it as a remote MCP server/connector with that header. If my client can't do this from the terminal, give me exact click-by-click steps.

2. Install the skills
   - Fetch ${SKILLS_BASE}/index.json and, for every entry in "skills", download ${SKILLS_BASE}/<name>/SKILL.md.
   - Save each one into your skills directory as <name>/SKILL.md (Claude Code: ~/.claude/skills/<name>/SKILL.md). If you have no skills directory, save them somewhere you will reload as instructions and tell me where.

3. Check web access
   - The skills tell you to read Google's official Search documentation (developers.google.com/search, support.google.com/webmasters) before advising. Confirm you can fetch web pages; if you can't, tell me which setting or tool enables it.

4. Verify
   - List the tools on the "${SERVER_NAME}" server; you should see about 60, including ${CHECK_TOOLS.join(", ")}. If you only see about ten coverage_* tools or get 403 "api:full", you're reaching an old WHYRANK server: stop and tell me.
   - Call coverage_overview and give me a two-line summary.
   - If you must restart to load the server or skills, tell me exactly what to restart.

Treat the integration key as a secret: never print it back in full or put it anywhere except the MCP header config.

${MCP_RULES}

When done, suggest I run the whyrank-seo-department skill for a first weekly review.`;
}

// Signature tools of the current server. The legacy coverage service only has the
// ten coverage_* tools, so their absence means the agent is on the wrong server.
const CHECK_TOOLS = ["list_tasks", "search_queries", "create_task", "agent_budget"];

const MCP_RULES = `How WHYRANK access works (read before you act):
- Use ONLY the tools on the "${SERVER_NAME}" MCP server. Never call the WHYRANK REST API (${MCP_URL.replace(/\/mcp$/, "")}/api/...) with curl or HTTP: the agent key isn't allowed there and gets 403 "This integration key doesn't have the 'api:full' scope". That 403 is expected for REST, not a missing permission. Never ask me to add api:full.
- The key's scopes are mcp (all tools), or mcp:read (read-only tools). A tool refused for scope says so in plain text, not 403.
- Changes to the live site need a task I approved: you file tasks (create_task), set them in_review, and I move them to In progress on the Tasks board. You can't approve tasks yourself.
- Jobs (run_sync, run_gsc_refresh, run_query_gap_analysis, run_ai_citation_check) spend a daily agent budget; check agent_budget first.`;

function updatePrompt() {
  return `Update my WHYRANK skills to the latest version and check your WHYRANK connection.

1. Skills
   - Fetch ${SKILLS_BASE}/index.json.
   - For every entry in "skills", download ${SKILLS_BASE}/<name>/SKILL.md and replace the matching <name>/SKILL.md in your skills directory (Claude Code: ~/.claude/skills/). Add any new skills.
   - If I've edited a skill locally, show me a short diff of my edits and re-apply them on top of the new version unless they conflict — ask me about conflicts.
   - Tell me which skills were added, updated, or unchanged.

2. Connection — don't touch the X-Integration-Key header
   - The "${SERVER_NAME}" MCP server URL must be exactly ${MCP_URL}. If yours differs, tell me and fix only the URL.
   - Reload the server and list its tools. You should see about 60, including ${CHECK_TOOLS.join(", ")}.
   - If you only see about ten coverage_* tools, or calls fail with 403 "api:full", you're reaching an old WHYRANK server, not this one. Stop and tell me: "The ${SERVER_NAME} MCP URL is reaching the legacy server; the WHYRANK team must route ${MCP_URL} to the current backend." Don't work around it with REST calls or a different key.
   - Otherwise call coverage_overview and agent_budget and give me a two-line summary.

${MCP_RULES}`;
}

function CopyButton({ text, label, disabled, variant = "primary" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const Icon = copied ? Check : Copy;
  if (variant === "ghost") {
    return (
      <button
        type="button"
        onClick={onCopy}
        disabled={disabled}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 rounded-md disabled:opacity-40 shrink-0 whitespace-nowrap"
      >
        <Icon size={12} strokeWidth={2} />
        {copied ? "Copied" : label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={disabled}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white rounded-lg disabled:opacity-40 transition-opacity hover:opacity-90"
      style={{ backgroundColor: PRIMARY }}
    >
      <Icon size={16} strokeWidth={2} />
      {copied ? "Copied!" : label}
    </button>
  );
}

function SetupTab({ integrationKey, generatingKey, onGenerateKey, keyError, onOpenHow }) {
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm">
        <div className="p-5 space-y-4">
          <div>
            <p className="text-sm font-bold text-zinc-900">Set up your agent</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Paste the setup prompt into your agent to connect WHYRANK over MCP and install its skills. It will guide you through any manual steps.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500">
            {CLIENTS.map(c => (
              <span key={c} className="flex items-center gap-1">
                <Bot size={12} strokeWidth={2} className="text-zinc-400" />
                {c}
              </span>
            ))}
            <span className="text-zinc-400">or any MCP client</span>
          </div>

          {integrationKey ? (
            <div className="flex flex-wrap items-center gap-4">
              <CopyButton text={setupPrompt(integrationKey)} label="Copy setup prompt" />
              <Link
                to="/dashboard/settings/integrations"
                className="flex items-center gap-1 text-xs text-zinc-500 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900"
              >
                Manage integration keys <ArrowUpRight size={12} />
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">
                Your agent gets its own integration key, limited to the MCP tools, fan-out and sitemap crawls. Create one to build the setup prompt — copy it right away, the key isn't shown again.
              </p>
              <button
                type="button"
                onClick={onGenerateKey}
                disabled={generatingKey}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: PRIMARY }}
              >
                <KeyRound size={16} strokeWidth={2} />
                {generatingKey ? "Creating…" : "Create agent key"}
              </button>
            </div>
          )}
          {keyError && <p className="text-xs text-red-500">{keyError}</p>}
          {integrationKey && (
            <p className="text-[10px] text-zinc-400">
              The prompt contains this agent's key and won't be shown again after you leave the page — paste it only into an agent you trust. Revoke it in Settings to disconnect just this agent.
            </p>
          )}
        </div>
        <div className="px-5 py-3.5 border-t border-zinc-100 bg-zinc-50 rounded-b-xl text-xs text-zinc-500">
          Once connected, ask your agent to run <span className="font-mono text-zinc-900">whyrank-seo-department</span> for a weekly review.{" "}
          <button type="button" onClick={onOpenHow} className="underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900">
            See how it works
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5 space-y-4">
        <div>
          <p className="text-sm font-bold text-zinc-900">Update your skills</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            Already connected? Paste the update prompt into your agent to get the latest WHYRANK skills (keeping your personal edits), check it reaches the right MCP server, and refresh its rules: MCP tools only, never the REST API, live changes only for approved tasks.
          </p>
        </div>
        <CopyButton text={updatePrompt()} label="Copy update prompt" />
      </div>

      <div className="pt-4 border-t border-zinc-200 flex items-center justify-between gap-3">
        <p className="text-[11px] text-zinc-500 truncate">
          MCP server URL for this instance: <span className="font-mono text-zinc-900">{MCP_URL}</span>
        </p>
        <CopyButton text={MCP_URL} label="Copy" variant="ghost" />
      </div>
    </div>
  );
}

// What each skill can do to the site. Drives the badge on its card.
const EFFECTS = {
  read: { label: "Read-only", cls: "bg-zinc-100 text-zinc-600" },
  tasks: { label: "Files tasks", cls: "bg-sky-50 text-sky-700" },
  report: { label: "Saves a report", cls: "bg-violet-50 text-violet-700" },
  draft: { label: "Creates a CMS draft", cls: "bg-zinc-100 text-zinc-700" },
  approval: { label: "Changes site after approval", cls: "bg-amber-50 text-amber-800" },
};

// Display info per skill in /skills/index.json, grouped by the job it does.
// A skill missing here still shows (under "More skills") with its index.json summary.
const SKILL_GROUPS = [
  {
    id: "plan",
    title: "Plan",
    hint: "Know where you stand and what to work on.",
    skills: {
      "whyrank-seo-department": {
        when: "Weekly. Your SEO team in one prompt: what moved, results of shipped work, new tasks to approve.",
        effect: "tasks",
        prompt: "Use the whyrank-seo-department skill. Run this week's SEO review: check what moved, follow up on open tasks, then file the 3–7 highest-value tasks on my WHYRANK Tasks board with evidence and the exact change. Don't change the site — I'll approve tasks first.",
      },
      "whyrank-coach": {
        when: "Not sure where to start. A short status and one next step.",
        effect: "read",
        prompt: "Use the whyrank-coach skill: summarize where my content coverage stands and pick my next step.",
      },
      "whyrank-report": {
        when: "End of the week or month, when you need a report to share.",
        effect: "report",
        prompt: "Use the whyrank-report skill: compare this month with the previous month and save the report to my WHYRANK Reports page.",
      },
      "whyrank-read-report": {
        when: "You have a saved report and want it explained.",
        effect: "read",
        prompt: "Use the whyrank-read-report skill: explain my latest saved report and give me the next steps.",
      },
    },
  },
  {
    id: "fix",
    title: "Fix",
    hint: "Find what holds pages back, then fix it with your approval.",
    skills: {
      "whyrank-coverage-audit": {
        when: "Pages missing from Google. Rebuilds Search Console's \"Why pages aren't indexed\" with a fix per reason.",
        effect: "read",
        prompt: "Use the whyrank-coverage-audit skill. Show me why my pages aren't indexed, read Google's official documentation for each reason, and give me a prioritized fix list with the doc link behind every fix.",
      },
      "whyrank-index-recovery": {
        when: "Articles Google crawled but won't index. Rewrites them for quality, then re-requests indexing.",
        effect: "approval",
        prompt: "Use the whyrank-index-recovery skill. Show me which published articles Google crawled but didn't index and why. Rewrite the top one with your own model, score the draft, and file it as a task for me to approve before anything is applied.",
      },
      "whyrank-article-rewrite": {
        when: "Articles flagged Needs rework on the Articles page: thin, or getting impressions but underperforming. Rewrites only their weak parts, in batches.",
        effect: "approval",
        prompt: "Use the whyrank-article-rewrite skill to work through my rework queue (articles that need rework, sorted by rework priority). Take the top 5: write each rewrite yourself, score it with score_article_draft, and file one task per article that beats its current score, all labeled plan:rework-<this month>. Show me a before → after table. Don't apply anything until I approve the tasks.",
      },
      "whyrank-internal-linking": {
        when: "Related articles that don't link to each other, or pages with no links in.",
        effect: "approval",
        prompt: "Use the whyrank-internal-linking skill: find the strongest missing internal links and file them as a task for me to approve.",
      },
    },
  },
  {
    id: "create",
    title: "Create",
    hint: "New copy written to be cited by AI answer engines.",
    skills: {
      "whyrank-copywriting": {
        when: "A new page or a section to write or improve. Pushes an unpublished draft to your CMS.",
        effect: "draft",
        prompt: "Use the whyrank-copywriting skill: write copy for a new page about <topic>, optimized to be cited by AI answer engines, and push it to my CMS as a draft once I approve it.",
      },
    },
  },
  {
    id: "paid",
    title: "Paid search",
    hint: "Reuse the queries that already find you to plan Google Ads.",
    skills: {
      "whyrank-google-ads-structure": {
        when: "Starting or restructuring a Google Ads Search account. Clusters your Search Console queries into campaigns and ad groups with keywords, negatives, landing pages and ad copy seeds.",
        effect: "report",
        prompt: "Use the whyrank-google-ads-structure skill. Build a Google Ads Search account structure from my Search Console queries: brand vs non-brand campaigns, tightly themed ad groups with exact and phrase keywords, a negative keyword list, the landing page for each ad group (flag weak ones), and responsive search ad headline and description seeds. Read Google Ads' official help pages first. Give me a Google Ads Editor CSV and save the plan to my WHYRANK Reports page. Ask me for brand names, target country and budget if you can't tell.",
      },
    },
  },
];

// Asks that don't need a skill — the agent just calls the right tool.
const QUICK_ASKS = [
  {
    title: "List my articles",
    prompt: "Use the whyrank MCP tool coverage_articles to list my published articles sorted by impressions, highest first. Show title, URL, clicks, impressions, position, content grade and whether it's indexed, then point out the three that most need work.",
  },
  {
    title: "Quick wins in search",
    prompt: "Use the whyrank MCP tool search_queries with opportunity=quick_win and task=none. Show the top 10 queries with the page that ranks, position and impressions, and say what would move each.",
  },
  {
    title: "Prioritize my Tasks board",
    prompt: "Use the whyrank-seo-department skill to prioritize my Tasks board: review open tasks and the Suggested items, check the data behind them, adjust priorities with a comment explaining each change, accept or create tasks for anything important that's missing, and give me the top 5 in order.",
  },
  {
    title: "Refresh my data",
    prompt: "Check sync_status, gsc_refresh_status and agent_budget. If Search Console data is more than a week old, run run_gsc_refresh, then run_query_gap_analysis when it finishes. Tell me what you started.",
  },
];

function EffectBadge({ effect }) {
  const e = EFFECTS[effect] || EFFECTS.read;
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${e.cls} whitespace-nowrap`}>{e.label}</span>;
}

function SkillCard({ name, info, summary }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4 flex flex-col gap-3" data-testid={`skill-${name}`}>
      <div className="flex items-start justify-between gap-3">
        <a
          href={`${SKILLS_BASE}/${name}/SKILL.md`}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-[13px] text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900 break-all"
          title="Open the skill file"
        >
          {name}
        </a>
        {info?.effect && <EffectBadge effect={info.effect} />}
      </div>
      <div className="space-y-1 flex-1">
        <p className="text-xs text-zinc-700">{summary}</p>
        {info?.when && <p className="text-xs text-zinc-500"><span className="font-medium text-zinc-600">Use when: </span>{info.when}</p>}
      </div>
      {info?.prompt && (
        <div>
          <CopyButton text={info.prompt} label="Copy prompt" variant="ghost" />
        </div>
      )}
    </div>
  );
}

function SkillsTab() {
  const [skills, setSkills] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${SKILLS_BASE}/index.json`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setSkills(d.skills || []))
      .catch(err => setError(`Couldn't load skills (${err.message}).`));
  }, []);

  const known = new Set(SKILL_GROUPS.flatMap(g => Object.keys(g.skills)));
  const byName = Object.fromEntries((skills || []).map(s => [s.name, s]));
  const others = (skills || []).filter(s => !known.has(s.name));

  return (
    <div className="space-y-8" data-testid="skills-tab">
      <p className="text-xs text-zinc-500">
        Your agent installed these with the setup prompt. Copy a prompt and paste it into your connected agent, or just
        ask in your own words and name the skill. Skills marked <EffectBadge effect="approval" /> only touch your site
        after you approve the task on the{" "}
        <Link to="/dashboard/tasks" className="underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900">Tasks board</Link>.
      </p>

      {error && <p className="text-xs text-red-500">{error}</p>}
      {!skills && !error && <p className="text-xs text-zinc-400">Loading…</p>}

      {skills && SKILL_GROUPS.map(group => {
        const items = Object.entries(group.skills).filter(([name]) => byName[name]);
        if (!items.length) return null;
        return (
          <section key={group.id} className="space-y-3">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{group.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{group.hint}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map(([name, info]) => (
                <SkillCard key={name} name={name} info={info} summary={byName[name].summary} />
              ))}
            </div>
          </section>
        );
      })}

      {others.length > 0 && (
        <section className="space-y-3">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">More skills</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {others.map(s => <SkillCard key={s.name} name={s.name} summary={s.summary} />)}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Quick asks</p>
          <p className="text-xs text-zinc-500 mt-0.5">No skill needed: your agent calls the right WHYRANK tool directly.</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm divide-y divide-zinc-100">
          {QUICK_ASKS.map(q => (
            <div key={q.title} className="px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-sm text-zinc-900">{q.title}</p>
              <CopyButton text={q.prompt} label="Copy prompt" variant="ghost" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const LOOP = [
  {
    icon: MessageSquare,
    title: "You ask",
    body: "Paste a skill prompt, or ask in your own words. Run the SEO department weekly.",
  },
  {
    icon: ListChecks,
    title: "Agent proposes",
    body: "It reads your WHYRANK data with its own model and files tasks with evidence and the exact change.",
  },
  {
    icon: ShieldCheck,
    title: "You approve",
    body: "Move a task to In progress on the Tasks board. Agents can't approve their own work.",
  },
  {
    icon: Rocket,
    title: "Agent ships and measures",
    body: "It applies the change, logs before → after on the task, and checks the search result about four weeks later.",
  },
];

const RULES = [
  {
    icon: ShieldCheck,
    title: "Live changes need your approval",
    body: "Titles, meta descriptions, internal links and rewrites only go live for a task someone on your team moved to In progress.",
  },
  {
    icon: History,
    title: "Everything is logged and reversible",
    body: "Each change is written on its task with the old and new value. A rewrite keeps the previous version of the article.",
  },
  {
    icon: Wallet,
    title: "A daily budget for agent jobs",
    body: "Syncs, Search Console analysis and AI-citation checks stop once the day's agent budget is spent. Ask your agent for agent_budget.",
  },
  {
    icon: Eye,
    title: "Read-only keys for watchers",
    body: "Give an agent that should only report a read-only key: it sees all data but can't file tasks, change anything or spend credits.",
    link: { to: "/dashboard/settings/integrations", label: "Create one in Settings" },
  },
];

const TIPS = [
  "Use your agent's strongest model. WHYRANK supplies the data; your agent does the thinking and writing.",
  "Name the skill in your ask (\"use whyrank-article-rewrite…\") so the agent follows its steps.",
  "Keep tasks small: one page or one change each, so results can be measured.",
  "After a WHYRANK update, paste the update prompt from the Set up tab to get the newest skills.",
];

function HowItWorksTab({ onOpenSkills }) {
  return (
    <div className="space-y-8" data-testid="how-it-works">
      <section className="space-y-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">The loop</p>
          <p className="text-xs text-zinc-500 mt-0.5">Your agent works like an SEO team that reports to you.</p>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {LOOP.map((step, i) => (
            <li key={step.title} className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span
                  className="w-5 h-5 rounded-full text-[11px] font-bold text-white flex items-center justify-center shrink-0"
                  style={{ backgroundColor: PRIMARY }}
                >
                  {i + 1}
                </span>
                <step.icon size={14} strokeWidth={2} className="text-zinc-400" />
              </div>
              <p className="text-sm font-bold text-zinc-900">{step.title}</p>
              <p className="text-xs text-zinc-500">{step.body}</p>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-4 pt-1">
          <button
            type="button"
            onClick={onOpenSkills}
            className="flex items-center gap-1 text-xs text-zinc-700 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900"
          >
            Pick a skill to start <ArrowRight size={12} />
          </button>
          <Link
            to="/dashboard/tasks"
            className="flex items-center gap-1 text-xs text-zinc-500 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900"
          >
            Open the Tasks board <ArrowUpRight size={12} />
          </Link>
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Built-in safeguards</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {RULES.map(r => (
            <div key={r.title} className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4 flex gap-3">
              <r.icon size={16} strokeWidth={2} className="text-zinc-400 mt-0.5 shrink-0" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900">{r.title}</p>
                <p className="text-xs text-zinc-500">{r.body}</p>
                {r.link && (
                  <Link
                    to={r.link.to}
                    className="inline-flex items-center gap-1 text-xs text-zinc-700 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900"
                  >
                    {r.link.label} <ArrowUpRight size={12} />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Tips</p>
        <ul className="space-y-2">
          {TIPS.map(t => (
            <li key={t} className="flex gap-2 text-xs text-zinc-600">
              <Check size={14} strokeWidth={2} className="mt-0.5 shrink-0" style={{ color: PRIMARY }} />
              {t}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

const TABS = [
  { id: "setup", label: "Set up your agent" },
  { id: "how", label: "How it works" },
  { id: "skills", label: "Skills" },
];

export default function AgentSetupPage() {
  const [tab, setTab] = useState("setup");
  // Keys are stored hashed, so an existing one can't be read back: the page makes a
  // fresh agent key and builds the prompt from it while it is still in memory.
  const [integrationKey, setIntegrationKey] = useState("");
  const [generatingKey, setGeneratingKey] = useState(false);
  const [keyError, setKeyError] = useState(null);

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    setKeyError(null);
    try {
      const result = await api.createIntegrationKey({ name: "AI agent", scopes: AGENT_SCOPES });
      setIntegrationKey(result.key);
    } catch (err) {
      console.error("[AgentSetup] createIntegrationKey failed:", { status: err.response?.status, message: err.message });
      setKeyError(err.response?.data?.detail || "Couldn't create a key. Try again from Settings.");
    } finally {
      setGeneratingKey(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Bot size={20} strokeWidth={2} className="text-zinc-900" />
        <div>
          <h1 className="text-xl font-bold text-zinc-900">Agent setup</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Use WHYRANK through the AI agent you already use. Set it up once, then ask it anything.
          </p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab === t.id ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "setup" && (
        <SetupTab
          integrationKey={integrationKey}
          generatingKey={generatingKey}
          onGenerateKey={handleGenerateKey}
          keyError={keyError}
          onOpenHow={() => setTab("how")}
        />
      )}
      {tab === "how" && <HowItWorksTab onOpenSkills={() => setTab("skills")} />}
      {tab === "skills" && <SkillsTab />}
    </div>
  );
}
